import React from 'react';

import { Tab as MuiTab, Tabs as MuiTabs, TabsProps as MuiTabsProps } from '@mui/material';
import { kebabCase } from 'lodash';

import { useMobileStore } from 'store/mobile.store';

import { CustomizedTooltip } from 'components/common/CustomizedTooltip/CustomizedTooltip';

import styles from './Tabs.module.scss';

export type TabVariant = 'navigation' | 'options';

export interface TabItem {
    /**
     * The label text for the tab
     */
    label: React.ReactNode;
    /**
     * The value for the tab (used for controlled state)
     */
    value: string;
    /**
     * Optional icon to display
     */
    icon?: React.ReactElement;
    /**
     * Whether the tab is disabled
     */
    disabled?: boolean;
    /**
     * Optional tooltip text to display on hover
     */
    tooltip?: string;
}

export interface TabsProps extends Omit<MuiTabsProps, 'variant' | 'onChange'> {
    /**
     * The variant of the tabs (navigation or options)
     */
    variant?: TabVariant;
    /**
     * Array of tab items
     */
    tabs: TabItem[];
    /**
     * The currently selected tab value
     */
    value: string;
    /**
     * Callback fired when the tab changes
     */
    onChange: (value: string) => void;
    /**
     * Should be scrollable
     */
    isScrollAble?: boolean;
    /**
     * Should add scroll buttons
     */
    isScrollButtons?: boolean;
}

/**
 * Custom Tabs component based on MUI Tabs
 * Implements design system styling from Figma Page Tab Option component
 * Supports light and dark themes via CSS variables
 */
export const Tabs: React.FC<TabsProps> = ({
    variant = 'navigation',
    tabs,
    value,
    onChange,
    className = '',
    isScrollAble,
    isScrollButtons,
    ...props
}) => {
    const isMobile = useMobileStore((state) => state.isMobile);

    const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
        onChange(newValue);
    };

    const tabsClass = [styles.tabs, styles[variant], className].filter(Boolean).join(' ');

    return (
        <MuiTabs
            className={tabsClass}
            value={value}
            onChange={handleChange}
            TabIndicatorProps={{
                className: styles.indicator,
            }}
            data-testid={'ds-tabs'}
            variant={isMobile || isScrollAble ? 'scrollable' : 'standard'}
            scrollButtons={isMobile || isScrollButtons ? 'auto' : false}
            {...props}
        >
            {tabs.map((tab, index) => {
                const label = tab.tooltip ? (
                        <CustomizedTooltip
                            triggerElement={
                                tab.icon ? (
                                    <span className={styles.tabTooltipInner}>
                                        <span className={styles.tabTooltipIcon}>{tab.icon}</span>
                                        {tab.label}
                                    </span>
                                ) : (
                                    <span>{tab.label}</span>
                                )
                            }
                            tooltipContent={tab.tooltip}
                        />
                    ) : (
                        tab.label
                    );

                return (
                    <MuiTab
                        key={tab.value}
                        className={styles.tab}
                        label={label}
                        value={tab.value}
                        icon={tab.tooltip && tab.icon ? undefined : tab.icon}
                        iconPosition={'start'}
                        disabled={tab.disabled}
                        data-testid={'tab number' + index}
                        data-track-id={kebabCase(`tab-${tab.value}`)}
                        disableRipple
                    />
                );
            })}
        </MuiTabs>
    );
};

export default Tabs;
